const API_URL = process.env.WP_API_URL || 'https://grvbuild.local/wp/wp-json/4wp/v1/grv';
const API_KEY = process.env.CMS_API_KEY || '';

function cmsApiHeaders() {
  const headers = {};
  if (API_KEY) {
    headers['X-CMS-API-Key'] = API_KEY;
  }
  return headers;
}

/**
 * Fetch CMS export — always fresh (admin edits visible without rebuild).
 */
export async function fetchSiteExport() {
  const res = await fetch(API_URL, {
    headers: cmsApiHeaders(),
    cache: 'no-store',
  });
  if (!res.ok) {
    throw new Error(`WP API ${res.status}: ${res.statusText}`);
  }
  return res.json();
}

/**
 * Normalize CMS slug path.
 * @param {string} slug
 */
export function normalizeCmsSlug(slug) {
  if (!slug || slug === '/') return '/';
  const trimmed = String(slug).trim();
  const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return withSlash.replace(/\/+$/, '') || '/';
}

/**
 * Legacy region URL aliases → canonical page slug.
 *
 * @param {import('@/types/cms').PageEntry[]} pages
 * @param {string} slug
 */
export function resolvePageSlugAlias(pages, slug) {
  const normalized = normalizeCmsSlug(slug);
  if (!pages?.length || normalized === '/') return null;

  const path = normalized.replace(/^\//, '');
  const parts = path.split('/').filter(Boolean);
  const last = parts[parts.length - 1] || '';

  for (const page of pages) {
    const pagePath = normalizeCmsSlug(page.slug).replace(/^\//, '');
    const pageParts = pagePath.split('/').filter(Boolean);
    const pageLast = pageParts[pageParts.length - 1] || '';

    if (pageLast === last) return page;
  }

  if (last.startsWith('region-')) {
    const geoSlug = last.replace(/^region-/, '');
    for (const page of pages) {
      if (page.geo_area_slug === geoSlug) return page;
    }
  }

  if (parts.length === 2 && parts[0] === 'region' && !parts[1].startsWith('region-')) {
    const legacyFlat = `/region-${parts[1]}`;
    const flatPage = pages.find((p) => normalizeCmsSlug(p.slug) === legacyFlat);
    if (flatPage) return flatPage;
  }

  return null;
}

export function findPageBySlug(cms, slug) {
  const pages = cms?.pages || [];
  const normalized = normalizeCmsSlug(slug);
  return pages.find((p) => normalizeCmsSlug(p.slug) === normalized)
    || resolvePageSlugAlias(pages, normalized)
    || null;
}

/** Internal CPT URL prefixes — not public CMS pages (faq_item, team_member, work_item). */
const RESERVED_SLUG_PREFIXES = new Set(['faq', 'team', 'work']);

/**
 * @param {string | string[] | undefined} slugParam Next `[...slug]` param
 */
export function isReservedInternalPath(slugParam) {
  if (!slugParam || (Array.isArray(slugParam) && slugParam.length === 0)) {
    return false;
  }
  const parts = Array.isArray(slugParam) ? slugParam : [slugParam];
  const first = String(parts[0] || '').toLowerCase();
  return RESERVED_SLUG_PREFIXES.has(first);
}

/** URL path → CMS slug (`pro-nas` | ['pro-nas'] → `/pro-nas`) */
export function pathToCmsSlug(slugParam) {
  if (!slugParam || (Array.isArray(slugParam) && slugParam.length === 0)) {
    return '/';
  }
  const parts = Array.isArray(slugParam) ? slugParam : [slugParam];
  return normalizeCmsSlug(`/${parts.join('/')}`);
}

/**
 * Region hub + city href from geo export.
 *
 * @param {import('@/types/cms').GeoCity} city
 */
export function regionCityHref(city) {
  if (city?.href) return normalizeCmsSlug(city.href);
  if (city?.slug) return `/region/region-${city.slug}`;
  return '/region';
}
